import { type RefObject, useEffect, useRef } from 'react'

/** Marks the element a chat scrolls inside; without one, or while it fits, the window scrolls. */
export const CHAT_SCROLLER_ATTR = 'data-chat-scroller'

function chatScroller(node: Element | null): HTMLElement | null {
  const scroller = node?.closest<HTMLElement>(`[${CHAT_SCROLLER_ATTR}]`)
  return scroller && scroller.scrollHeight > scroller.clientHeight ? scroller : null
}

/** Scrolls the chat so `target` sits just below the top of whatever the chat scrolls in. */
export function scrollChatTo(node: Element | null, target: Element) {
  const scroller = chatScroller(node)
  const top = target.getBoundingClientRect().top
  if (scroller) {
    const offset = top - scroller.getBoundingClientRect().top
    scroller.scrollTo({ top: scroller.scrollTop + offset - 20, behavior: 'smooth' })
  } else {
    window.scrollTo({ top: window.scrollY + top - 20, behavior: 'smooth' })
  }
}

export function scrollChatToBottom(node: Element | null) {
  const scroller = chatScroller(node)
  if (scroller) scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' })
  else window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
}

/**
 * When new messages arrive, scrolls to the top of the first new one.
 */
export function useChatAutoScroll({
  containerRef,
  messageCount,
  actionKey,
  disabled,
  deps,
}: {
  containerRef: RefObject<HTMLDivElement | null>
  messageCount: number
  actionKey?: string
  disabled?: boolean
  deps: unknown[]
}) {
  const prevMessageCount = useRef(messageCount)
  const scrollTargetIndex = useRef(-1)
  const prevActionKey = useRef(actionKey)

  useEffect(() => {
    if (disabled) return
    const timeout = setTimeout(() => scrollChatToBottom(containerRef.current), 200)
    return () => clearTimeout(timeout)
  }, [disabled, containerRef])

  useEffect(() => {
    if (disabled || !containerRef.current) return

    // New messages arrived — lock scroll target to the first new one
    if (messageCount > prevMessageCount.current && scrollTargetIndex.current === -1) {
      scrollTargetIndex.current = prevMessageCount.current
    }
    prevMessageCount.current = messageCount

    // Action changed — scroll to the last message (action renders right after it)
    const actionChanged = actionKey !== prevActionKey.current
    prevActionKey.current = actionKey

    const messageEls = containerRef.current.querySelectorAll('[data-chat-message]')

    if (actionChanged && scrollTargetIndex.current === -1) {
      scrollTargetIndex.current = messageEls.length - 1
    }

    if (scrollTargetIndex.current === -1) return

    const targetMsg = messageEls[scrollTargetIndex.current]
    if (!targetMsg) return

    const t1 = setTimeout(() => scrollChatTo(containerRef.current, targetMsg), 200)

    // Reset so the next batch can pick a new target
    scrollTargetIndex.current = -1

    return () => {
      clearTimeout(t1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}
